/**
 * Time Machine #114 Phase 5 — pure response parser for the Lambda's
 * `getEventLogByDocumentId` and `getEventLogForCrew` methods. Both return
 * an `events` array; this parser handles either method and produces a
 * uniform success / error shape for the caller.
 *
 * Pass `expectedMethod` so the parser can verify the response envelope's
 * `message.method` matches what the caller expected — catches dispatch
 * bugs where the Lambda routed to the wrong handler.
 */

const VALID_METHODS = [
  "getEventLogByDocumentId",
  "getEventLogForCrew",
  // Phase 6 — Lambda v05-09-2026-01-QRY01+ supports filtering Attributes
  // EventLog by (payrollNo, date) cell key. Drives TimeMachineAttributeHistoryModal.
  "getEventLogForAttributeCell",
];

export function parseEventLogResponse(rawResponse, expectedMethod) {
  if (!VALID_METHODS.includes(expectedMethod)) {
    return {
      ok: false,
      kind: "error",
      reason: `parseEventLogResponse: expectedMethod must be one of ${VALID_METHODS.join(", ")} (got ${JSON.stringify(expectedMethod)})`,
      raw: rawResponse,
    };
  }

  if (rawResponse == null || typeof rawResponse !== "object") {
    return {
      ok: false,
      kind: "error",
      reason: "Response is not an object",
      raw: rawResponse,
    };
  }

  const { message, _debugVersion, errorType, errorMessage } = rawResponse;

  // Lambda runtime error
  if (errorType || errorMessage) {
    return {
      ok: false,
      kind: "error",
      reason: errorMessage || errorType || "Lambda error (no message)",
      lambdaErrorType: errorType || null,
      raw: rawResponse,
    };
  }

  // API Gateway error string
  if (typeof message === "string") {
    return {
      ok: false,
      kind: "error",
      reason: `API Gateway: ${message}`,
      raw: rawResponse,
    };
  }

  if (!message || typeof message !== "object") {
    return {
      ok: false,
      kind: "error",
      reason: "Response missing message envelope",
      raw: rawResponse,
    };
  }

  if (message.method !== expectedMethod) {
    return {
      ok: false,
      kind: "error",
      reason: `Expected message.method "${expectedMethod}" (got ${JSON.stringify(message.method)})`,
      raw: rawResponse,
    };
  }

  const result = message.result;
  if (!result || typeof result !== "object") {
    return {
      ok: false,
      kind: "error",
      reason: "Response missing message.result",
      raw: rawResponse,
    };
  }

  // Lambda-side validation error (e.g. invalid documentId hex, bad date)
  if (result.error) {
    return {
      ok: false,
      kind: "error",
      reason: result.error,
      raw: rawResponse,
      lambdaVersion: _debugVersion ?? null,
    };
  }

  // Success — events array must be present
  if (!Array.isArray(result.events)) {
    return {
      ok: false,
      kind: "error",
      reason: "Response result.events is missing or not an array",
      raw: rawResponse,
    };
  }

  return {
    ok: true,
    kind: "success",
    events: result.events,
    meta: result.meta ?? null,
    lambdaVersion: _debugVersion ?? null,
  };
}
